/**
 * CareCaseService
 * Service for managing care/counselling cases with list/detail views, dashboard stats,
 * leader/member assignment, notes and files
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for care case CRUD operations, paged list views, detail views,
 * leader/member assignment, the case note audit trail and file attachments
 */
export class CareCaseService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? CareCaseService.DEFAULT_SERVICE_PATH,
            basePath: CareCaseService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Soft-delete many care cases off the list in one call (spam/junk cleanup). Missing ones are skipped
     * server-side, so this reports partial success honestly. Gated by AllowBulkDelete.Cases (default OFF) and
     * restricted to ActsCareAdmin — a 403 means the setting is off. `delete(id)` (inherited) removes one.
     * POST api/care-cases/delete-many
     * @param ids - The case IDs to delete
     * @returns How many cases were actually removed
     */
    async deleteMany(ids) {
        const response = await this.deleteManyRaw(ids);
        return response.data?.removed ?? 0;
    }
    deleteManyRaw(ids) {
        const url = this.buildUrl('delete-many');
        return this.http.post(url, { ids });
    }
    /**
     * Get the paged case list view (filtered)
     * @param filter - Optional filter parameters
     * @returns Paged case list view response
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        if (!response.data) {
            throw new Error('Failed to retrieve care case list view');
        }
        return response.data;
    }
    /**
     * Get the paged case list view (filtered) with full response
     * @param filter - Optional filter parameters
     * @returns ResponseData with paged case list view response
     */
    listViewRaw(filter) {
        const url = this.buildUrl('list-view');
        return this.http.post(url, filter ?? {});
    }
    /**
     * Get case detail view by ID (leaders, counselees, sessions, notes, files)
     * @param id - The case ID
     * @returns Care case model view
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Care case view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get case detail view by ID with full response
     * @param id - The case ID
     * @returns ResponseData with care case model view
     */
    getViewRaw(id) {
        const url = this.buildUrl(`view/${id}`);
        return this.http.get(url);
    }
    /**
     * Get care dashboard statistics
     * @returns Dashboard statistics for the care admin home
     */
    async dashboardStats() {
        const response = await this.dashboardStatsRaw();
        if (!response.data) {
            throw new Error('Failed to retrieve care dashboard stats');
        }
        return response.data;
    }
    /**
     * Get care dashboard statistics with full response
     * @returns ResponseData with dashboard statistics
     */
    dashboardStatsRaw() {
        const url = this.buildUrl('dashboard-stats');
        return this.http.get(url);
    }
    // ---------------------------------------------------------------------------
    // Overview aggregates (admin) — breakdown charts, monthly trend, unassigned
    // ---------------------------------------------------------------------------
    /**
     * Get open/total case counts grouped by care need
     * @returns Array of need breakdown buckets, descending by openCount
     */
    async statsByNeed() {
        const response = await this.statsByNeedRaw();
        return response.data ?? [];
    }
    /**
     * Get open/total case counts grouped by care need with full response
     * @returns ResponseData with array of need breakdown buckets
     */
    statsByNeedRaw() {
        const url = this.buildUrl('stats/by-need');
        return this.http.get(url);
    }
    /**
     * Get open/total case counts grouped by care zone
     * @returns Array of zone breakdown buckets, descending by openCount
     */
    async statsByZone() {
        const response = await this.statsByZoneRaw();
        return response.data ?? [];
    }
    /**
     * Get open/total case counts grouped by care zone with full response
     * @returns ResponseData with array of zone breakdown buckets
     */
    statsByZoneRaw() {
        const url = this.buildUrl('stats/by-zone');
        return this.http.get(url);
    }
    /**
     * Get open/total case counts grouped by care area
     * @returns Array of area breakdown buckets, descending by openCount
     */
    async statsByArea() {
        const response = await this.statsByAreaRaw();
        return response.data ?? [];
    }
    /**
     * Get open/total case counts grouped by care area with full response
     * @returns ResponseData with array of area breakdown buckets
     */
    statsByAreaRaw() {
        const url = this.buildUrl('stats/by-area');
        return this.http.get(url);
    }
    /**
     * Get open/total case counts grouped by location
     * @returns Array of location breakdown buckets, descending by openCount
     */
    async statsByLocation() {
        const response = await this.statsByLocationRaw();
        return response.data ?? [];
    }
    /**
     * Get open/total case counts grouped by location with full response
     * @returns ResponseData with array of location breakdown buckets
     */
    statsByLocationRaw() {
        const url = this.buildUrl('stats/by-location');
        return this.http.get(url);
    }
    /**
     * Get the monthly opened-vs-closed case trend
     * @param months - Number of months to include, most recent last (default 12)
     * @returns Array of monthly trend rows, oldest first
     */
    async statsMonthly(months = 12) {
        const response = await this.statsMonthlyRaw(months);
        return response.data ?? [];
    }
    /**
     * Get the monthly opened-vs-closed case trend with full response
     * @param months - Number of months to include (default 12)
     * @returns ResponseData with array of monthly trend rows
     */
    statsMonthlyRaw(months = 12) {
        const params = new URLSearchParams();
        params.append('months', months.toString());
        const url = this.buildUrl(`stats/monthly?${params.toString()}`);
        return this.http.get(url);
    }
    /**
     * Get open cases that have no owning leader
     * @param take - Maximum rows to return (default 10)
     * @returns Array of unassigned case list rows, oldest first
     */
    async unassigned(take = 10) {
        const response = await this.unassignedRaw(take);
        return response.data ?? [];
    }
    /**
     * Get open cases that have no owning leader with full response
     * @param take - Maximum rows to return (default 10)
     * @returns ResponseData with array of unassigned case list rows
     */
    unassignedRaw(take = 10) {
        const params = new URLSearchParams();
        params.append('take', take.toString());
        const url = this.buildUrl(`unassigned?${params.toString()}`);
        return this.http.get(url);
    }
    /**
     * Assign a leader to a case
     * @param caseId - The case ID
     * @param userId - The leader user ID
     * @param isLead - Whether the leader is the designated lead
     * @returns True if the assignment succeeded
     */
    async assignLeader(caseId, userId, isLead) {
        const response = await this.assignLeaderRaw(caseId, userId, isLead);
        return response.success;
    }
    /**
     * Assign a leader to a case with full response
     * @param caseId - The case ID
     * @param userId - The leader user ID
     * @param isLead - Whether the leader is the designated lead
     * @returns ResponseData (success/fail)
     */
    assignLeaderRaw(caseId, userId, isLead) {
        const url = this.buildUrlWithParams(`${caseId}/leaders/${userId}`, {
            isLead,
        });
        return this.http.post(url, {});
    }
    /**
     * Remove a leader from a case
     * @param caseId - The case ID
     * @param userId - The leader user ID
     * @returns True if the removal succeeded
     */
    async removeLeader(caseId, userId) {
        const response = await this.removeLeaderRaw(caseId, userId);
        return response.success;
    }
    /**
     * Remove a leader from a case with full response
     * @param caseId - The case ID
     * @param userId - The leader user ID
     * @returns ResponseData (success/fail)
     */
    removeLeaderRaw(caseId, userId) {
        const url = this.buildUrl(`${caseId}/leaders/${userId}`);
        return this.http.delete(url);
    }
    /**
     * Get the leaders assigned to a case
     * @param caseId - The case ID
     * @returns Array of case leader links
     */
    async leaders(caseId) {
        const response = await this.leadersRaw(caseId);
        return response.data ?? [];
    }
    /**
     * Get the leaders assigned to a case with full response
     * @param caseId - The case ID
     * @returns ResponseData with array of case leader links
     */
    leadersRaw(caseId) {
        const url = this.buildUrl(`${caseId}/leaders`);
        return this.http.get(url);
    }
    /**
     * Change only the case's status.
     * PATCH api/care-cases/{id}/change-status/{newStatus}
     * Closing the case (-1) still publishes the CareCaseClosedEvent server-side.
     * @param caseId - The case ID
     * @param newStatus - The new StatusID
     * @returns The updated care case
     */
    async changeStatus(caseId, newStatus) {
        const response = await this.changeStatusRaw(caseId, newStatus);
        if (!response.data) {
            throw new Error(`Failed to change status for care case ${caseId}`);
        }
        return response.data;
    }
    /**
     * Change only the case's status with full response
     * @param caseId - The case ID
     * @param newStatus - The new StatusID
     * @returns ResponseData with the updated care case
     */
    changeStatusRaw(caseId, newStatus) {
        const url = this.buildUrl(`${caseId}/change-status/${newStatus}`);
        return this.http.patch(url);
    }
    /**
     * Add a counselee (member) to a case
     * @param caseId - The case ID
     * @param memberId - The member ID
     * @param isMain - Whether the member is the primary counselee
     * @returns True if the member was added
     */
    async addMember(caseId, memberId, isMain) {
        const response = await this.addMemberRaw(caseId, memberId, isMain);
        return response.success;
    }
    /**
     * Add a counselee (member) to a case with full response
     * @param caseId - The case ID
     * @param memberId - The member ID
     * @param isMain - Whether the member is the primary counselee
     * @returns ResponseData (success/fail)
     */
    addMemberRaw(caseId, memberId, isMain) {
        const url = this.buildUrlWithParams(`${caseId}/members/${memberId}`, {
            isMain,
        });
        return this.http.post(url, {});
    }
    /**
     * Get the counselees (members) on a case
     * @param caseId - The case ID
     * @returns Array of case member links
     */
    async members(caseId) {
        const response = await this.membersRaw(caseId);
        return response.data ?? [];
    }
    /**
     * Get the counselees (members) on a case with full response
     * @param caseId - The case ID
     * @returns ResponseData with array of case member links
     */
    membersRaw(caseId) {
        const url = this.buildUrl(`${caseId}/members`);
        return this.http.get(url);
    }
    /**
     * Update a counselee link on a case (e.g. primary flag / consent)
     * @param caseId - The case ID
     * @param memberId - The member ID
     * @param data - Partial counselee payload (only supplied fields are applied)
     * @returns The updated case member link
     */
    async updateMember(caseId, memberId, data) {
        const response = await this.updateMemberRaw(caseId, memberId, data);
        if (!response.data) {
            throw new Error(`Failed to update member ${memberId} on care case ${caseId}`);
        }
        return response.data;
    }
    /**
     * Update a counselee link on a case with full response
     * @param caseId - The case ID
     * @param memberId - The member ID
     * @param data - Partial counselee payload
     * @returns ResponseData with the updated case member link
     */
    updateMemberRaw(caseId, memberId, data) {
        const url = this.buildUrl(`${caseId}/members/${memberId}`);
        return this.http.put(url, data);
    }
    /**
     * Remove a counselee (member) from a case
     * @param caseId - The case ID
     * @param memberId - The member ID
     * @returns True if removed successfully
     */
    async removeMember(caseId, memberId) {
        const response = await this.removeMemberRaw(caseId, memberId);
        return response.success;
    }
    /**
     * Remove a counselee (member) from a case with full response
     * @param caseId - The case ID
     * @param memberId - The member ID
     * @returns ResponseData (success/fail)
     */
    removeMemberRaw(caseId, memberId) {
        const url = this.buildUrl(`${caseId}/members/${memberId}`);
        return this.http.delete(url);
    }
    /**
     * Repair: re-import counselees this case is missing from its originating request (when approval failed
     * to carry someone across). Each is match-or-created from the request subject's FULL details (contacts,
     * gender, DOB, member status) server-side — not just their name. Idempotent; subjects already on the case
     * are skipped.
     * @param caseId - The case ID
     * @param subjectIDs - Optional subset of subject IDs to import; omit to import every missing subject
     * @returns The counselee links that were added
     */
    async importFromRequest(caseId, subjectIDs, includeRequester) {
        const response = await this.importFromRequestRaw(caseId, subjectIDs, includeRequester);
        return response.data ?? [];
    }
    /**
     * Re-import missing counselees from the originating request, with full response.
     * @param caseId - The case ID
     * @param subjectIDs - Optional subset of subject IDs to import
     * @param includeRequester - Also import the requester/on-behalf person from the request header (who has
     *   no subject id), badged as the requester — covers requests whose subject list is empty
     * @returns ResponseData with the counselee links added
     */
    importFromRequestRaw(caseId, subjectIDs, includeRequester) {
        const url = this.buildUrl(`${caseId}/import-from-request`);
        return this.http.post(url, { subjectIDs: subjectIDs ?? null, includeRequester: includeRequester ?? false });
    }
    // ---------------------------------------------------------------------------
    // Need custom data (captured need-level custom-field answers). Mirrors the
    // registration course-custom-data surface: lets the case page display (and the
    // office recover) the answers a request carried at approval.
    // ---------------------------------------------------------------------------
    /**
     * Get the captured need custom-field answers on a case (with depth: typed value + field definition)
     * @param caseId - The case ID
     * @returns Array of case custom-data ModelViews
     */
    async customData(caseId) {
        const response = await this.customDataRaw(caseId);
        return response.data ?? [];
    }
    /**
     * Get the captured need custom-field answers on a case with full response
     * @param caseId - The case ID
     * @returns ResponseData with array of case custom-data ModelViews
     */
    customDataRaw(caseId) {
        const url = this.buildUrl(`${caseId}/custom-data`);
        return this.http.get(url);
    }
    /**
     * Capture/replace a need custom-field answer on a case (upsert by field + member)
     * @param caseId - The case ID
     * @param data - The custom-field value to save
     * @returns The saved case custom-data row
     */
    async addCustomData(caseId, data) {
        const response = await this.addCustomDataRaw(caseId, data);
        if (!response.data) {
            throw new Error(`Failed to add custom data to care case ${caseId}`);
        }
        return response.data;
    }
    /**
     * Capture/replace a need custom-field answer on a case with full response
     * @param caseId - The case ID
     * @param data - The custom-field value to save
     * @returns ResponseData with the saved case custom-data row
     */
    addCustomDataRaw(caseId, data) {
        const url = this.buildUrl(`${caseId}/custom-data`);
        return this.http.post(url, data);
    }
    /**
     * Update a captured need custom-field answer (only supplied fields are applied)
     * @param dataId - The care case custom-data ID
     * @param data - Partial custom-data payload
     * @returns The updated case custom-data row
     */
    async updateCustomData(dataId, data) {
        const response = await this.updateCustomDataRaw(dataId, data);
        if (!response.data) {
            throw new Error(`Failed to update care case custom data ${dataId}`);
        }
        return response.data;
    }
    /**
     * Update a captured need custom-field answer with full response
     * @param dataId - The care case custom-data ID
     * @param data - Partial custom-data payload
     * @returns ResponseData with the updated case custom-data row
     */
    updateCustomDataRaw(dataId, data) {
        const url = this.buildUrl(`custom-data/${dataId}`);
        return this.http.put(url, data);
    }
    /**
     * Get the audit-trail notes for a case
     * @param caseId - The case ID
     * @returns Array of case notes
     */
    async notes(caseId) {
        const response = await this.notesRaw(caseId);
        return response.data ?? [];
    }
    /**
     * Get the audit-trail notes for a case with full response
     * @param caseId - The case ID
     * @returns ResponseData with array of case notes
     */
    notesRaw(caseId) {
        const url = this.buildUrl(`${caseId}/notes`);
        return this.http.get(url);
    }
    /**
     * Add a note to a case
     * @param caseId - The case ID
     * @param data - Note payload
     * @returns The created case note
     */
    async addNote(caseId, data) {
        const response = await this.addNoteRaw(caseId, data);
        if (!response.data) {
            throw new Error(`Failed to add note to care case ${caseId}`);
        }
        return response.data;
    }
    /**
     * Add a note to a case with full response
     * @param caseId - The case ID
     * @param data - Note payload
     * @returns ResponseData with the created case note
     */
    addNoteRaw(caseId, data) {
        const url = this.buildUrl(`${caseId}/notes`);
        return this.http.post(url, data);
    }
    /**
     * Update a case note
     * @param caseId - The case ID
     * @param noteId - The note ID
     * @param data - Partial note payload (only supplied fields are applied)
     * @returns The updated case note
     */
    async updateNote(caseId, noteId, data) {
        const response = await this.updateNoteRaw(caseId, noteId, data);
        if (!response.data) {
            throw new Error(`Failed to update note ${noteId} on care case ${caseId}`);
        }
        return response.data;
    }
    /**
     * Update a case note with full response
     * @param caseId - The case ID
     * @param noteId - The note ID
     * @param data - Partial note payload
     * @returns ResponseData with the updated case note
     */
    updateNoteRaw(caseId, noteId, data) {
        const url = this.buildUrl(`${caseId}/notes/${noteId}`);
        return this.http.put(url, data);
    }
    /**
     * Delete a case note
     * @param caseId - The case ID
     * @param noteId - The note ID
     * @returns True if deleted successfully
     */
    async deleteNote(caseId, noteId) {
        const response = await this.deleteNoteRaw(caseId, noteId);
        return response.success;
    }
    /**
     * Delete a case note with full response
     * @param caseId - The case ID
     * @param noteId - The note ID
     * @returns ResponseData (success/fail)
     */
    deleteNoteRaw(caseId, noteId) {
        const url = this.buildUrl(`${caseId}/notes/${noteId}`);
        return this.http.delete(url);
    }
    /**
     * Attach a file to a case
     * @param caseId - The case ID
     * @param file - File payload
     * @returns The created case file
     */
    async addFile(caseId, file) {
        const response = await this.addFileRaw(caseId, file);
        if (!response.data) {
            throw new Error(`Failed to attach file to care case ${caseId}`);
        }
        return response.data;
    }
    /**
     * Attach a file to a case with full response
     * @param caseId - The case ID
     * @param file - File payload
     * @returns ResponseData with the created case file
     */
    addFileRaw(caseId, file) {
        const url = this.buildUrl(`${caseId}/files`);
        return this.http.post(url, file);
    }
    /**
     * Get the files attached to a case
     * @param caseId - The case ID
     * @returns Array of case files
     */
    async files(caseId) {
        const response = await this.filesRaw(caseId);
        return response.data ?? [];
    }
    /**
     * Get the files attached to a case with full response
     * @param caseId - The case ID
     * @returns ResponseData with array of case files
     */
    filesRaw(caseId) {
        const url = this.buildUrl(`${caseId}/files`);
        return this.http.get(url);
    }
    /**
     * Update a case file
     * @param caseId - The case ID
     * @param fileId - The file ID
     * @param data - Partial file payload (only supplied fields are applied)
     * @returns The updated case file
     */
    async updateFile(caseId, fileId, data) {
        const response = await this.updateFileRaw(caseId, fileId, data);
        if (!response.data) {
            throw new Error(`Failed to update file ${fileId} on care case ${caseId}`);
        }
        return response.data;
    }
    /**
     * Update a case file with full response
     * @param caseId - The case ID
     * @param fileId - The file ID
     * @param data - Partial file payload
     * @returns ResponseData with the updated case file
     */
    updateFileRaw(caseId, fileId, data) {
        const url = this.buildUrl(`${caseId}/files/${fileId}`);
        return this.http.put(url, data);
    }
    /**
     * Delete a case file
     * @param caseId - The case ID
     * @param fileId - The file ID
     * @returns True if deleted successfully
     */
    async deleteFile(caseId, fileId) {
        const response = await this.deleteFileRaw(caseId, fileId);
        return response.success;
    }
    /**
     * Delete a case file with full response
     * @param caseId - The case ID
     * @param fileId - The file ID
     * @returns ResponseData (success/fail)
     */
    deleteFileRaw(caseId, fileId) {
        const url = this.buildUrl(`${caseId}/files/${fileId}`);
        return this.http.delete(url);
    }
}
CareCaseService.DEFAULT_SERVICE_PATH = 'ac-counselling';
CareCaseService.BASE_PATH = 'api/care-cases';
//# sourceMappingURL=CareCaseService.js.map